// src/pages/StudentProfile.jsx — student settings, badge showcase, and lifetime stats (/profile).
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { doc, updateDoc, getDocs, collection } from "firebase/firestore";
import { motion } from "framer-motion";
import {
  HiPencil, HiCheck, HiX, HiAcademicCap,
  HiStar, HiLightningBolt, HiFire, HiBadgeCheck,
} from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import { useAuth } from "../context/AuthContext";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import AnimatedCounter from "../components/student/AnimatedCounter";
import { BADGES, TIER_STYLES, levelProgress } from "../lib/gamification";
import { DEFAULT_WEEKLY_GOAL } from "../lib/goals";

const BOARDS = [
  { id: "state", name: "State Board", subtitle: "Tamil Nadu State Board" },
  { id: "cbse", name: "CBSE", subtitle: "Central Board" },
  { id: "competitive", name: "Competitive", subtitle: "JEE · NEET · TNPSC" },
];
const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };
const HANDLE_RE = /^[a-zA-Z0-9_]{2,20}$/;

const sortByOrder = (arr) => [...arr].sort((a, b) => (a.order || 0) - (b.order || 0));

export default function StudentProfile() {
  const { user, profile, refreshProfile } = useAuth();
  const [topicStats, setTopicStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handle editing
  const [editingHandle, setEditingHandle] = useState(false);
  const [handleInput, setHandleInput] = useState("");
  const [handleError, setHandleError] = useState("");
  const [handleSaving, setHandleSaving] = useState(false);

  // Weekly goal editing
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalError, setGoalError] = useState("");

  // Board/grade change
  const [changingClass, setChangingClass] = useState(false);
  const [newBoard, setNewBoard] = useState("");
  const [grades, setGrades] = useState([]);
  const [newGrade, setNewGrade] = useState("");
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [classSaving, setClassSaving] = useState(false);
  const [classError, setClassError] = useState("");

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "students", user.uid, "topicStats"));
        if (active) setTopicStats(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error("Profile stats load failed", e); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [user]);

  // Fetch grades when board changes
  useEffect(() => {
    if (!newBoard) { setGrades([]); setNewGrade(""); return; }
    setLoadingGrades(true);
    getDocs(collection(db, "curriculum", newBoard, "grades"))
      .then((snap) => setGrades(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() })))))
      .catch(() => setGrades([]))
      .finally(() => setLoadingGrades(false));
    setNewGrade("");
  }, [newBoard]);

  const ownedBadges = new Set(profile?.badges || []);
  const lp = levelProgress(profile?.xp || 0);

  const bestSubject = useMemo(() => {
    const map = {};
    for (const t of topicStats) {
      if (!t.subject) continue;
      if (!map[t.subject]) map[t.subject] = { totalCorrect: 0, totalQuestions: 0 };
      map[t.subject].totalCorrect += t.totalCorrect || 0;
      map[t.subject].totalQuestions += t.totalQuestions || 0;
    }
    let best = null, bestAcc = 0;
    for (const [subject, d] of Object.entries(map)) {
      if (d.totalQuestions === 0) continue;
      const acc = d.totalCorrect / d.totalQuestions;
      if (acc > bestAcc) { bestAcc = acc; best = subject; }
    }
    return best;
  }, [topicStats]);

  const joinDate = useMemo(() => {
    if (!user?.metadata?.creationTime) return null;
    return new Date(user.metadata.creationTime).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [user]);

  // Badge progress hints
  function badgeHint(id, profile) {
    const remaining = {
      "first-quiz": Math.max(0, 1 - (profile?.totalAttempts || 0)),
      "ten-quizzes": Math.max(0, 10 - (profile?.totalAttempts || 0)),
      "streak-7": Math.max(0, 7 - (profile?.streak?.current || 0)),
      "level-5": null,
      "perfect-score": null,
      "topic-master": null,
    };
    const r = remaining[id];
    if (r === 0) return null;
    if (id === "first-quiz") return "Take your first quiz!";
    if (id === "ten-quizzes" && r !== null) return `${r} more quiz${r !== 1 ? "zes" : ""} to unlock`;
    if (id === "streak-7" && r !== null) return `${r} more day${r !== 1 ? "s" : ""} of streak`;
    if (id === "level-5") return "Reach Level 5 to unlock";
    if (id === "perfect-score") return "Score 100% on any quiz";
    if (id === "topic-master") return "90%+ accuracy on a topic (2+ attempts)";
    return null;
  }

  async function saveHandle() {
    setHandleError("");
    if (!HANDLE_RE.test(handleInput)) {
      setHandleError("2–20 chars, letters, numbers, and underscores only.");
      return;
    }
    setHandleSaving(true);
    try {
      await updateDoc(doc(db, "students", user.uid), { handle: handleInput });
      await refreshProfile();
      setEditingHandle(false);
    } catch (e) {
      setHandleError("Couldn't save. Please try again.");
    } finally {
      setHandleSaving(false);
    }
  }

  async function saveGoal() {
    setGoalError("");
    const n = parseInt(goalInput, 10);
    if (!Number.isFinite(n) || n < 50 || n > 100000) {
      setGoalError("Enter a goal between 50 and 100000 XP.");
      return;
    }
    setGoalSaving(true);
    try {
      await updateDoc(doc(db, "students", user.uid), { weeklyGoal: n });
      await refreshProfile();
      setEditingGoal(false);
    } catch (e) {
      setGoalError("Couldn't save. Please try again.");
    } finally {
      setGoalSaving(false);
    }
  }

  async function saveClass() {
    if (!newBoard || !newGrade) return;
    setClassSaving(true);
    setClassError("");
    try {
      await updateDoc(doc(db, "students", user.uid), { board: newBoard, grade: newGrade });
      await refreshProfile();
      setChangingClass(false);
    } catch (e) {
      setClassError("Couldn't save. Please try again.");
    } finally {
      setClassSaving(false);
    }
  }

  if (!profile) return <Loader />;

  const initials = (profile.handle || "S").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="My Profile" description="Your profile, badges, and stats." canonical="/profile" />

      {/* Light in-shell page header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl font-black text-white shadow-lg shadow-indigo-200">
              {initials}
            </div>
            <div>
              <p className="text-indigo-500 text-xs font-semibold uppercase tracking-widest mb-0.5">
                Level {profile.level || 1}
              </p>
              <h1 className="text-2xl sm:text-3xl font-black text-gray-800">{profile.handle || "Student"}</h1>
              {joinDate && <p className="text-gray-400 text-sm mt-0.5">Member since {joinDate}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Lifetime Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Quizzes Taken", value: profile.totalAttempts || 0, icon: HiAcademicCap, color: "text-blue-500" },
            { label: "Total XP", value: profile.xp || 0, icon: HiLightningBolt, color: "text-indigo-500" },
            { label: "Best Subject", raw: bestSubject || "—", icon: HiStar, color: "text-amber-500" },
            { label: "Longest Streak", value: profile.streak?.longest || 0, suffix: " days", icon: HiFire, color: "text-orange-500" },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4"
            >
              <s.icon className={`text-xl ${s.color} mb-2`} />
              <p className="text-2xl font-black text-gray-800">
                {s.raw !== undefined
                  ? <span className="capitalize">{s.raw}</span>
                  : <><AnimatedCounter value={s.value || 0} />{s.suffix || ""}</>
                }
              </p>
              <p className="text-gray-400 text-xs font-semibold mt-0.5">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Settings */}
          <div className="lg:col-span-1 space-y-4">

            {/* Edit Handle */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-extrabold text-gray-800 mb-3">Display Name</h2>
              {editingHandle ? (
                <div>
                  <input
                    value={handleInput}
                    onChange={(e) => setHandleInput(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    placeholder="your_handle"
                    maxLength={20}
                  />
                  {handleError && <p className="text-xs text-red-500 mt-1">{handleError}</p>}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={saveHandle}
                      disabled={handleSaving}
                      className="flex-1 bg-indigo-600 text-white text-sm font-bold py-2 rounded-xl hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors flex items-center justify-center gap-1"
                    >
                      {handleSaving ? (
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <><HiCheck /> Save</>
                      )}
                    </button>
                    <button
                      onClick={() => { setEditingHandle(false); setHandleError(""); }}
                      className="px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <HiX className="text-gray-400" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800">{profile.handle || "—"}</span>
                  <button
                    onClick={() => { setHandleInput(profile.handle || ""); setEditingHandle(true); }}
                    className="flex items-center gap-1 text-xs text-indigo-600 font-semibold hover:underline"
                  >
                    <HiPencil /> Edit
                  </button>
                </div>
              )}
            </div>

            {/* Board & Grade */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-extrabold text-gray-800 mb-3">Class & Board</h2>
              {changingClass ? (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Select Board</p>
                  <div className="grid grid-cols-1 gap-1.5 mb-3">
                    {BOARDS.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => setNewBoard(b.id)}
                        className={`text-left border-2 rounded-xl p-2.5 transition-all ${newBoard === b.id ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}
                      >
                        <p className="font-bold text-sm text-gray-800">{b.name}</p>
                        <p className="text-[11px] text-gray-400">{b.subtitle}</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-gray-500 mb-1">Select Class</p>
                  <select
                    value={newGrade}
                    onChange={(e) => setNewGrade(e.target.value)}
                    disabled={!newBoard || loadingGrades}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-50 mb-3"
                  >
                    <option value="">{loadingGrades ? "Loading…" : !newBoard ? "Select a board first" : "Select your class"}</option>
                    {grades.map((g) => <option key={g.id} value={g.label}>{g.name}</option>)}
                  </select>
                  {classError && <p className="text-xs text-red-500 mb-2">{classError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={saveClass}
                      disabled={!newBoard || !newGrade || classSaving}
                      className="flex-1 bg-indigo-600 text-white text-sm font-bold py-2 rounded-xl hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors flex items-center justify-center gap-1"
                    >
                      {classSaving ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><HiCheck /> Save</>}
                    </button>
                    <button onClick={() => { setChangingClass(false); setClassError(""); }} className="px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      <HiX className="text-gray-400" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">{profile.board ? BOARD_LABELS[profile.board] || profile.board : "—"}</p>
                    {profile.grade && <p className="text-xs text-gray-400">Class {profile.grade}</p>}
                  </div>
                  <button
                    onClick={() => { setNewBoard(profile.board || ""); setChangingClass(true); }}
                    className="flex items-center gap-1 text-xs text-indigo-600 font-semibold hover:underline"
                  >
                    <HiPencil /> Change
                  </button>
                </div>
              )}
            </div>

            {/* Weekly Goal */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-extrabold text-gray-800 mb-3">Weekly XP Goal</h2>
              {editingGoal ? (
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      placeholder="500"
                      min={50}
                      step={50}
                    />
                    <span className="text-sm font-semibold text-gray-400">XP</span>
                  </div>
                  {goalError && <p className="text-xs text-red-500 mt-1">{goalError}</p>}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={saveGoal}
                      disabled={goalSaving}
                      className="flex-1 bg-indigo-600 text-white text-sm font-bold py-2 rounded-xl hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors flex items-center justify-center gap-1"
                    >
                      {goalSaving ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><HiCheck /> Save</>}
                    </button>
                    <button onClick={() => { setEditingGoal(false); setGoalError(""); }} className="px-3 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      <HiX className="text-gray-400" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">{profile.weeklyGoal || DEFAULT_WEEKLY_GOAL} XP / week</p>
                    <p className="text-xs text-gray-400">Track it on your dashboard.</p>
                  </div>
                  <button
                    onClick={() => { setGoalInput(String(profile.weeklyGoal || DEFAULT_WEEKLY_GOAL)); setEditingGoal(true); }}
                    className="flex items-center gap-1 text-xs text-indigo-600 font-semibold hover:underline"
                  >
                    <HiPencil /> Edit
                  </button>
                </div>
              )}
            </div>

            {/* XP Progress */}
            <div className="bg-gradient-to-br from-slate-800 to-indigo-900 rounded-2xl p-5 text-white">
              <p className="text-indigo-300 text-xs font-semibold uppercase tracking-wide mb-1">Level {profile.level || 1}</p>
              <p className="text-3xl font-black mb-1"><AnimatedCounter value={profile.xp || 0} /> XP</p>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-1">
                <motion.div
                  className="h-full bg-gradient-to-r from-indigo-400 to-blue-400 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${lp.pct}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </div>
              <p className="text-indigo-300 text-xs">{lp.toNext} XP to Level {lp.nextLevel}</p>
              {(profile.level || 1) >= 5 && (
                <Link
                  to={`/certificate?type=level&value=${profile.level || 1}`}
                  className="inline-flex items-center gap-1.5 mt-3 bg-white/15 hover:bg-white/25 text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-colors"
                >
                  🏆 Level {profile.level} certificate
                </Link>
              )}
            </div>
          </div>

          {/* Right: Badge Showcase */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-extrabold text-gray-800 text-lg mb-4">Badge Showcase</h2>
              {loading ? (
                <div className="py-8"><Loader /></div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {BADGES.map((b) => {
                    const earned = ownedBadges.has(b.id);
                    const hint = !earned ? badgeHint(b.id, profile) : null;
                    const ts = TIER_STYLES[b.tier] || TIER_STYLES.gold;
                    return (
                      <motion.div
                        key={b.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className={`rounded-2xl p-4 border flex flex-col items-center text-center transition-all ${
                          earned ? `${ts.bg} ${ts.border} shadow-md` : "bg-gray-50 border-gray-100"
                        }`}
                      >
                        <span className={`text-4xl block mb-2 ${earned ? "" : "grayscale opacity-40"}`}>{b.icon}</span>
                        <p className={`font-extrabold text-sm ${earned ? ts.text : "text-gray-400"}`}>{b.name}</p>
                        <p className={`text-[11px] mt-1 leading-tight ${earned ? ts.text : "text-gray-400"}`}>
                          {earned ? b.description : hint || b.description}
                        </p>
                        {earned && (
                          <>
                            <span className={`mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${ts.chip}`}>
                              Earned ✓
                            </span>
                            <Link
                              to={`/certificate?type=badge&value=${b.id}`}
                              className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-indigo-600 hover:underline"
                            >
                              <HiBadgeCheck className="text-xs" /> Certificate
                            </Link>
                          </>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
